import React from 'react';
import { FileText, Lock, Shield } from 'lucide-react';
import { PROCESSING_LABELS, type ProcessingJob } from '@/types/batch';

interface BatchOrderSummaryProps {
  job: ProcessingJob;
  processingRate: number;
}

export const BatchOrderSummary: React.FC<BatchOrderSummaryProps> = ({ job, processingRate }) => {
  return (
    <>
      <div className="ca-card  p-4">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-4 h-4 text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-900">{job.fileName ?? job.file?.name ?? 'Uploaded file'}</p>
              <p className="text-xs text-slate-500">{job.totalRows.toLocaleString()} total records</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center mt-3 pt-3 border-t border-slate-200">
            <div>
              <p className="text-sm font-semibold text-slate-900">{job.processableRows.toLocaleString()}</p>
              <p className="text-xs text-slate-600">Processable</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">{(job.totalRows - job.processableRows).toLocaleString()}</p>
              <p className="text-xs text-slate-600">Invalid</p>
            </div>
          </div>
        </div>

        <dl className="space-y-3 text-sm text-slate-600">
          <div className="flex justify-between">
            <dt>Processing type</dt>
            <dd className="font-medium text-slate-900">{PROCESSING_LABELS[job.processingType]}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Rate per successful row</dt>
            <dd className="font-medium text-slate-900">${processingRate.toFixed(2)}</dd>
          </div>
          {job.aiOverview && (
            <div className="flex justify-between text-xs text-slate-600">
              <dt>AI overview add-on</dt>
              <dd className="font-medium text-slate-900">+$0.03 / row</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt>Estimated charge</dt>
            <dd className="font-medium text-slate-900">${job.estimatedCost.toFixed(2)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Maximum authorization</dt>
            <dd className="font-semibold text-slate-900">${job.maxPossibleCost.toFixed(2)}</dd>
          </div>
        </dl>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-slate-900 flex gap-3">
          <Shield className="w-4 h-4 mt-0.5" />
          <span>
            We authorize the maximum possible amount while processing runs. Once the batch completes, the charge adjusts
            to the exact number of successful records and any remaining hold is released immediately.
          </span>
        </div>
      </div>

      <div className="ca-card  p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Secure processing
        </h3>
        <ul className="space-y-2 text-xs text-slate-600">
          <li>Data encrypted at rest and in transit</li>
          <li>Charges only captured after job success metrics confirmed</li>
        </ul>
      </div>
    </>
  );
};
